import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useSignInFlow } from '@mystudioclass/auth';
import { Video, User, Eye, EyeOff } from '../components/Icons';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export default function LoginPage() {
  const { isLoaded, createSignIn, error } = useSignInFlow();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');

  // Force light mode for public pages
  useEffect(() => {
    const previousTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      }
    };
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoaded) {
      return;
    }

    setLoading(true);

    // Store returnTo BEFORE login (auth may cause page reload)
    if (returnTo) {
      localStorage.setItem('postLoginRedirect', returnTo);
    }

    try {
      const result = await createSignIn({
        identifier: email,
        password,
      });

      if (result?.error) {
        throw new Error(result.error.message || 'Invalid email or password');
      }

      if (!result?.data?.user && !result?.data?.session) {
        throw new Error('Invalid email or password');
      }

      toast.success('Login successful!');

      // If we get here (no page reload), handle redirect ourselves
      const savedRedirect = localStorage.getItem('postLoginRedirect');
      if (savedRedirect) {
        localStorage.removeItem('postLoginRedirect');
        window.location.href = savedRedirect;
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      localStorage.removeItem('postLoginRedirect');
      toast.error(err.message || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: 'var(--bg-base)' }}
    >
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
              boxShadow: '0 0 40px var(--primary-30)'
            }}
          >
            <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>My Studio Class</h1>
          <p style={{ color: 'var(--text-muted)' }} className="mt-2">Customer Portal</p>
        </div>

        {/* Login Form */}
        <div
          className="rounded-2xl shadow-xl p-8"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-muted)'
          }}
        >
          <div
            className="flex items-center justify-center mb-6 p-3 rounded-lg"
            style={{ background: 'var(--bg-muted)' }}
          >
            <User className="w-5 h-5 mr-2" style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Customer Sign In</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="label">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="customer@example.com"
                required
                disabled={!isLoaded || loading}
              />
            </div>

            <div>
              <div className="flex justify-between items-center">
                <label className="label">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-sm transition-colors"
                  style={{ color: 'var(--primary)' }}
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.color = 'var(--primary)'}
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-10"
                  placeholder="••••••••"
                  required
                  disabled={!isLoaded || loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                  onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!isLoaded || loading}
              className="btn btn-primary w-full"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center space-y-2">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-medium transition-colors"
                style={{ color: 'var(--primary)' }}
                onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-hover)'}
                onMouseOut={(e) => e.currentTarget.style.color = 'var(--primary)'}
              >
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
